import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';

interface ChecklistItem {
  id: string;
  category: string;
  item: string;
  vehicle_class: string;
  is_custom: boolean;
  requires_photo?: boolean;
}

const Checklists: React.FC = () => {
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filterClass, setFilterClass] = useState<string>('all');
  const [formData, setFormData] = useState({
    category: '',
    item: '',
    vehicle_class: 'both',
    requires_photo: false
  });

  useEffect(() => {
    fetchChecklists();
  }, []);

  const fetchChecklists = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/checklists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChecklistItems(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching checklists:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/checklists`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowModal(false);
      setFormData({ category: '', item: '', vehicle_class: 'both', requires_photo: false });
      fetchChecklists();
    } catch (error) {
      console.error('Error creating checklist item:', error);
      alert('Failed to create checklist item');
    }
  };

  const deleteItem = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this checklist item?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/checklists/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchChecklists();
    } catch (error) {
      console.error('Error deleting checklist item:', error);
      alert('Failed to delete checklist item');
    }
  };

  const filteredItems = checklistItems.filter(item => {
    if (filterClass === 'all') return true;
    return item.vehicle_class === filterClass;
  });

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Inspection Checklists</h1>
        <div className="flex gap-4">
          <select
            value={filterClass}
            onChange={(e) => setFilterClass(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="all">All Classes</option>
            <option value="class_1">Class 1 (Articulated)</option>
            <option value="class_2">Class 2 (Rigid)</option>
          </select>
          <button
            onClick={() => setShowModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Add Custom Check
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check Item</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle Class</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                  No checklist items found
                </td>
              </tr>
            ) : (
              filteredItems.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">
                      {item.category}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {item.item}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {item.vehicle_class === 'class_1' ? 'Class 1 (Articulated)' : 'Class 2 (Rigid)'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded ${
                      item.is_custom ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {item.is_custom ? 'Custom' : 'Default'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {item.is_custom && (
                      <button
                        onClick={() => deleteItem(item.id)}
                        className="text-red-600 hover:text-red-800 font-medium"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add Custom Check Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-xl font-bold mb-4">Add Custom Check</h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Category</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="e.g., Bodywork, Engine, Tyres"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Check Item</label>
                <textarea
                  value={formData.item}
                  onChange={(e) => setFormData({ ...formData, item: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Describe what needs to be checked..."
                  rows={3}
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Vehicle Class</label>
                <select
                  value={formData.vehicle_class}
                  onChange={(e) => setFormData({ ...formData, vehicle_class: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                >
                  <option value="both">Both Classes</option>
                  <option value="class_2">Class 2 (Rigid)</option>
                  <option value="class_1">Class 1 (Articulated)</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.requires_photo}
                    onChange={(e) => setFormData({ ...formData, requires_photo: e.target.checked })}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm font-medium">Require photo for this check</span>
                </label>
                <p className="text-xs text-gray-500 mt-1">Driver must take a photo to complete this check</p>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Add Check
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checklists;
